import { Button } from "@akuna/ui";
// src/pages/erp/fiscal/GeneralTab.tsx
import { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Loader2, AlertCircle, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@akuna/ui";
import { supabase } from "@/integrations/supabase/client";
import { GeneralSchema, type GeneralFormValues } from "./fiscal-settings.schemas";
import type { FiscalTabProps } from "./fiscal-settings.types";
import { FocusMunicipalityAutocomplete } from "@/components/erp/FocusMunicipalityAutocomplete";

/** Remove máscara CNPJ, mantém alfanumérico uppercase */
const stripCnpj = (v: string) => v.replace(/[.\-/\s]/g, "").toUpperCase();

/** Aplica máscara XX.XXX.XXX/XXXX-XX (aceita letras e números) */
function maskCnpj(raw: string): string {
  const v = stripCnpj(raw).slice(0, 14);
  if (v.length <= 2) return v;
  if (v.length <= 5) return `${v.slice(0, 2)}.${v.slice(2)}`;
  if (v.length <= 8) return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5)}`;
  if (v.length <= 12) return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8)}`;
  return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12)}`;
}

const digitsOnly = (v: string) => v.replace(/\D/g, "");

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function GeneralTab({ cnpj: cnpjProp, focusData, focusEmpresaId, localDefaults, clinicId, customerId }: FiscalTabProps) {
  const { t } = useTranslation("erp");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const isNew = focusData === null;
  const cnpjMissing = !cnpjProp;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<GeneralFormValues>({
    resolver: zodResolver(GeneralSchema),
    defaultValues: {
      cnpj: stripCnpj(cnpjProp),
      nome: "",
      nome_fantasia: "",
      email: "",
      telefone: "",
      regime_tributario: "1",
      inscricao_estadual: "",
      inscricao_municipal: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cep: "",
      municipio: "",
      codigo_municipio_emitente: "",
      uf: "",
      arquivo_certificado_base64: "",
      senha_certificado: "",
    },
  });

  useEffect(() => {
    if (!focusData) return;
    reset({
      cnpj: stripCnpj(focusData.cnpj ?? cnpjProp),
      nome: focusData.nome ?? "",
      nome_fantasia: focusData.nome_fantasia ?? "",
      email: focusData.email ?? "",
      telefone: focusData.telefone ?? "",
      regime_tributario: String(focusData.regime_tributario ?? "1") as GeneralFormValues["regime_tributario"],
      inscricao_estadual: focusData.inscricao_estadual ?? "",
      inscricao_municipal: focusData.inscricao_municipal ?? "",
      logradouro: focusData.logradouro ?? "",
      numero: focusData.numero ?? "",
      complemento: focusData.complemento ?? "",
      bairro: focusData.bairro ?? "",
      cep: digitsOnly(focusData.cep ?? ""),
      municipio: focusData.municipio ?? "",
      codigo_municipio_emitente: localDefaults?.codigo_municipio_emitente ?? localDefaults?.municipal_code ?? "",
      uf: focusData.uf ?? "",
      arquivo_certificado_base64: "",
      senha_certificado: "",
    });
  }, [focusData, cnpjProp, localDefaults, reset]);

  const processFile = async (file: File) => {
    const base64 = await fileToBase64(file);
    setValue("arquivo_certificado_base64", base64, { shouldValidate: true });
    setSelectedFileName(file.name);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const onSubmit = async (values: GeneralFormValues) => {
    // values.cnpj already stripped by Zod transform
    const cnpjRaw = values.cnpj;

    // Persist CNPJ to clinics table if it changed or was missing
    if (cnpjRaw !== stripCnpj(cnpjProp)) {
      const { error: clinicError } = await supabase
        .from("clinics")
        .update({ cnpj: cnpjRaw })
        .eq("id", clinicId);
      if (clinicError) {
        toast.error(t("fiscalSettings.general.saveError"));
        return;
      }
    }

    const payload: Record<string, unknown> = {
      nome: values.nome,
      nome_fantasia: values.nome_fantasia || undefined,
      cnpj: cnpjRaw,
      email: values.email,
      telefone: digitsOnly(values.telefone),
      regime_tributario: Number(values.regime_tributario),
      inscricao_estadual: values.inscricao_estadual || undefined,
      inscricao_municipal: values.inscricao_municipal || undefined,
      logradouro: values.logradouro,
      numero: values.numero,
      complemento: values.complemento || undefined,
      bairro: values.bairro,
      cep: digitsOnly(values.cep),
      municipio: values.municipio,
      uf: values.uf.toUpperCase(),
    };

    if (values.arquivo_certificado_base64) {
      payload.arquivo_certificado_base64 = values.arquivo_certificado_base64;
      payload.senha_certificado = values.senha_certificado;
    }

    // FocusNFe empresa API (sempre produção):
    //   POST /v2/empresas      → criar  (sem id)
    //   PUT  /v2/empresas/:id  → alterar (com id numérico)
    const isCreating = !focusEmpresaId;
    const { data: fnData, error: fnError } = await supabase.functions.invoke(
      "fiscal-manage-empresa",
      {
        body: isCreating
          ? { method: "POST", payload }
          : { method: "PUT", focus_empresa_id: focusEmpresaId, payload },
      },
    );

    if (fnError) {
      toast.error(t("fiscalSettings.general.saveError"));
      return;
    }

    const { error: settingsError } = await supabase
      .from("clinic_fiscal_settings")
      .upsert(
        {
          clinic_id: clinicId,
          customer_id: customerId,
          ...(isCreating && fnData?.id ? { focus_empresa_id: String(fnData.id) } : {}),
          cnpj: cnpjRaw,
          company_name: values.nome,
          trade_name: values.nome_fantasia || null,
          state_registration: values.inscricao_estadual || null,
          municipal_registration: values.inscricao_municipal || null,
          tax_regime_code: values.regime_tributario,
          telefone_emitente: digitsOnly(values.telefone),
          address_street: values.logradouro,
          address_number: values.numero,
          complemento_emitente: values.complemento || null,
          address_neighborhood: values.bairro,
          address_zip_code: digitsOnly(values.cep),
          municipal_name: values.municipio,
          codigo_municipio_emitente: values.codigo_municipio_emitente,
          municipal_code: values.codigo_municipio_emitente,
          uf: values.uf.toUpperCase(),
        },
        { onConflict: "clinic_id" },
      );

    if (settingsError) {
      toast.error(t("fiscalSettings.general.saveError"));
      return;
    }

    toast.success(t("fiscalSettings.general.saveSuccess"));
    setValue("arquivo_certificado_base64", "");
    setValue("senha_certificado", "");
    setSelectedFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const hasCertFile = !!watch("arquivo_certificado_base64");
  const regimeTributario = watch("regime_tributario");
  const uf = watch("uf");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {isNew && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t("fiscalSettings.general.notRegisteredBanner")}</AlertDescription>
        </Alert>
      )}

      {/* Identidade */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label>{t("fiscalSettings.general.fields.nome")}</Label>
          <Input {...register("nome")} disabled={isSubmitting} />
          {errors.nome && <p className="text-sm text-destructive">{errors.nome.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.nomeFantasia")}</Label>
          <Input {...register("nome_fantasia")} disabled={isSubmitting} />
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.cnpj")}</Label>
          {cnpjMissing ? (
            <>
              <Controller
                control={control}
                name="cnpj"
                render={({ field }) => (
                  <Input
                    value={maskCnpj(field.value)}
                    onChange={(e) => field.onChange(stripCnpj(e.target.value))}
                    onBlur={field.onBlur}
                    disabled={isSubmitting}
                    placeholder="00.000.000/0000-00"
                    maxLength={18}
                  />
                )}
              />
              {errors.cnpj && <p className="text-sm text-destructive">{errors.cnpj.message}</p>}
            </>
          ) : (
            <Input value={maskCnpj(cnpjProp)} readOnly />
          )}
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.inscricaoEstadual")}</Label>
          <Input {...register("inscricao_estadual")} disabled={isSubmitting} />
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.inscricaoMunicipal")}</Label>
          <Input {...register("inscricao_municipal")} disabled={isSubmitting} />
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.email")}</Label>
          <Input type="email" {...register("email")} disabled={isSubmitting} />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.telefone")}</Label>
          <Input {...register("telefone")} disabled={isSubmitting} />
          {errors.telefone && <p className="text-sm text-destructive">{errors.telefone.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.regimeTributario")}</Label>
          <Select
            value={regimeTributario}
            onValueChange={(v) => setValue("regime_tributario", v as GeneralFormValues["regime_tributario"], { shouldValidate: true })}
            disabled={isSubmitting}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">{t("fiscalSettings.general.regimeTributario.simples")}</SelectItem>
              <SelectItem value="2">{t("fiscalSettings.general.regimeTributario.simplexExcesso")}</SelectItem>
              <SelectItem value="3">{t("fiscalSettings.general.regimeTributario.normal")}</SelectItem>
              <SelectItem value="4">{t("fiscalSettings.general.regimeTributario.mei")}</SelectItem>
            </SelectContent>
          </Select>
          {errors.regime_tributario && <p className="text-sm text-destructive">{errors.regime_tributario.message}</p>}
        </div>
      </div>

      {/* Endereço */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label>{t("fiscalSettings.general.fields.logradouro")}</Label>
          <Input {...register("logradouro")} disabled={isSubmitting} />
          {errors.logradouro && <p className="text-sm text-destructive">{errors.logradouro.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.numero")}</Label>
          <Input {...register("numero")} disabled={isSubmitting} />
          {errors.numero && <p className="text-sm text-destructive">{errors.numero.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.complemento")}</Label>
          <Input {...register("complemento")} disabled={isSubmitting} />
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.bairro")}</Label>
          <Input {...register("bairro")} disabled={isSubmitting} />
          {errors.bairro && <p className="text-sm text-destructive">{errors.bairro.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.cep")}</Label>
          <Input {...register("cep")} disabled={isSubmitting} />
          {errors.cep && <p className="text-sm text-destructive">{errors.cep.message}</p>}
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>{t("fiscalSettings.general.fields.municipio")}</Label>
          <Input {...register("municipio")} disabled={isSubmitting} />
          {errors.municipio && <p className="text-sm text-destructive">{errors.municipio.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.codigoMunicipio")}</Label>
          <Controller
            control={control}
            name="codigo_municipio_emitente"
            render={({ field }) => (
              <FocusMunicipalityAutocomplete
                value={field.value}
                state={uf}
                onChange={(value, municipality) => {
                  field.onChange(value);
                  if (municipality?.name) {
                    setValue("municipio", municipality.name, { shouldDirty: true, shouldValidate: true });
                  }
                  if (municipality?.state) {
                    setValue("uf", municipality.state, { shouldDirty: true, shouldValidate: true });
                  }
                }}
                placeholder={t("fiscalSettings.general.municipalitySearch.placeholder")}
                disabled={isSubmitting}
              />
            )}
          />
          {errors.codigo_municipio_emitente && (
            <p className="text-sm text-destructive">{errors.codigo_municipio_emitente.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.uf")}</Label>
          <Input {...register("uf")} maxLength={2} disabled={isSubmitting} />
          {errors.uf && <p className="text-sm text-destructive">{errors.uf.message}</p>}
        </div>
      </div>

      {/* Certificado */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>{t("fiscalSettings.general.fields.certificado")}</Label>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pfx,.p12"
            onChange={handleFileChange}
            disabled={isSubmitting}
            className="hidden"
          />

          {!isNew && !hasCertFile ? (
            <div className="flex flex-col gap-4 rounded-lg border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{t("fiscalSettings.general.certificateUploadedTitle")}</p>
                  <p className="text-sm text-muted-foreground">{t("fiscalSettings.general.certificateReplaceHint")}</p>
                </div>
              </div>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-muted disabled:opacity-50"
              >
                <Upload className="h-4 w-4" />
                {t("fiscalSettings.general.certificateReplaceButton")}
              </button>
            </div>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !isSubmitting && fileInputRef.current?.click()}
              className={[
                "flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 transition-colors",
                isSubmitting ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50",
              ].join(" ")}
            >
              {selectedFileName ? (
                <>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">{selectedFileName}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t("fiscalSettings.general.certificateSelected")}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <Upload className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">{t("fiscalSettings.general.certificateDropTitle")}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t("fiscalSettings.general.certificateDropSubtitle")}</p>
                  </div>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-muted disabled:opacity-50"
                  >
                    <Upload className="h-4 w-4" />
                    {t("fiscalSettings.general.certificateSelectButton")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {hasCertFile && (
          <div className="space-y-2 md:max-w-xs">
            <Label>{t("fiscalSettings.general.fields.senhaCertificado")}</Label>
            <Input
              type="password"
              {...register("senha_certificado")}
              disabled={isSubmitting}
            />
            {errors.senha_certificado && (
              <p className="text-sm text-destructive">{errors.senha_certificado.message}</p>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {t("fiscalSettings.general.save")}
        </Button>
      </div>
    </form>
  );
}
